import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Accordion, Button, Card, Col, Form, Row } from "react-bootstrap";
import IEquipment, { IEquipmentCreateOrUpdate, IEquipmentInitResult } from "models/sql/IEquipment";
import EquipmentService from "services/EquipmentService";
import moment from 'moment';
import IProduct, { IProductFilters } from "models/sql/IProduct";
import ProductService from "services/ProductService";
import IFireStation, { IFireStationFilters } from "models/sql/IFireStation";
import FireStationService from "services/FireStationService";
import IDealer, { IDealerFilters } from "models/sql/IDealer";
import DealerService from "services/DealerService";
import IFireDepartment, { IFireDepartmentFilters } from "models/sql/IFireDepartment";
import FireDepartmentService from "services/FireDepartmentService";
import { generateLog } from "utilities/MiscUtilities";
import ISalesRep, { ISalesRepFilters } from "models/sql/ISalesRep";
import SalesRepService from "services/SalesRepService";
import CountryChooser from "components/CountryChooser";
import MarkdownEditor from "components/MarkdownEditor";
import { useBeforeUnload } from "utilities/UIUtilities";
import DPSEntitySelector from "components/DPSEntitySelector";
import StateChooser from "components/StateChooser";
import DetailFooter from "components/DetailFooter";
import { IFileAttachment } from "models/IFileAttachment";

interface IEquipmentDetailProps {
    baseData?: IEquipmentInitResult;
}

const EquipmentDetail = (props: IEquipmentDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [product, setProduct] = useState<IProduct>();
    const [dealer, setDealer] = useState<IDealer>();
    const [salesRep, setSalesRep] = useState<ISalesRep>();
    const [fireStation, setFireStation] = useState<IFireStation>();
    const [fireDepartment, setFireDepartment] = useState<IFireDepartment>();
    const [equipment, setEquipment] = useState<IEquipment | null>({
        equipmentId: -1,
        serialNumber: "",
        dealerId: undefined,
        fireDepartmentId: undefined,
        extraData: "",
        notes: "",
        parentEquipmentId: undefined,
        productId: undefined,
        registrationDate: undefined,
        startedService: undefined,
        stationId: undefined,
        registrationEmail: "",
        registrationPostalCode: "",
        registrationName: "",
        created: undefined,
        demoRequestDate: undefined,
        demoApprovalDate: undefined,
        demoStartDate: undefined,
        demoEndDate: undefined,
        demoRequestEmail: "",
        demoRequestName: "",
        systemDataJson: "",
        location: "",
        building: "",
        region: "",
        country: "",
        city: "",
        departmentName: "",
        outOfServiceDate: undefined,
        repAssignDate: undefined,
        salesRepId: undefined,
        score: undefined,
        shippingNotes: "",
        stationNumber: "",
        address1: "",
        address2: "",
        state: "",
        postalCode: "",
        contactName: "",
        contactTitle: "",
        contactEmail: "",
        qaCompleteDate: undefined,
        receivedDate: undefined,
        shippedDate: undefined,
        demoDuration: undefined
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const [attachments, setAttachments] = useState<IFileAttachment[]>();

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await EquipmentService.GetAsync(accessToken, id);

                    if (result.record) {
                        setEquipment(result.record);

                        if (result.record.systemDataJson) {
                            const systemData = JSON.parse(result.record.systemDataJson);
                            if (systemData.files) {
                                setAttachments(systemData.files);
                            }
                        }

                        if (result.record.productId !== null && result.record.productId !== undefined) {
                            const productResult = await ProductService.GetAsync(accessToken, result.record.productId);

                            if (productResult.record) {
                                setProduct(productResult.record);
                            }
                        }

                        if (result.record.stationId !== null && result.record.stationId !== undefined) {
                            const stationResult = await FireStationService.GetAsync(accessToken, result.record.stationId);

                            if (stationResult.record) {
                                setFireStation(stationResult.record);
                            }
                        }

                        if (result.record.fireDepartmentId !== null && result.record.fireDepartmentId !== undefined) {
                            const departmentResult = await FireDepartmentService.GetAsync(accessToken, result.record.fireDepartmentId);

                            if (departmentResult.record) {
                                setFireDepartment(departmentResult.record);
                            }
                        }

                        if (result.record.dealerId !== null && result.record.dealerId !== undefined) {
                            const dealerResult = await DealerService.GetAsync(accessToken, result.record.dealerId);

                            if (dealerResult.record) {
                                setDealer(dealerResult.record);
                            }
                        }

                        if (result.record.salesRepId !== null && result.record.salesRepId !== undefined) {
                            const salesRepResult = await SalesRepService.GetAsync(accessToken, result.record.salesRepId);

                            if (salesRepResult.record) {
                                setSalesRep(salesRepResult.record);
                            }
                        }
                    }
                    else {
                        setEquipment(null);
                        globalContext.showErrorMessage("Unable to load Equipment");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (equipment && equipment.equipmentId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await EquipmentService.DeleteAsync(accessToken, equipment.equipmentId);
            globalContext.setLoading(false);
            navigate("/Equipment");
        }
    };

    const submitFormAsync = async () => {
        if (equipment) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IEquipmentCreateOrUpdate = {
                equipmentId: equipment.equipmentId,
                dealerId: equipment.dealerId,
                fireDepartmentId: equipment.fireDepartmentId,
                stationId: equipment.stationId,
                productId: equipment.productId,
                serialNumber: equipment.serialNumber,
                parentEquipmentId: equipment.parentEquipmentId,
                startedService: equipment.startedService || undefined,
                registrationDate: equipment.registrationDate || undefined,
                extraData: equipment.extraData,
                notes: equipment.notes,
                registrationEmail: equipment.registrationEmail,
                registrationName: equipment.registrationName,
                registrationPostalCode: equipment.registrationPostalCode,
                created: equipment.created || undefined,
                demoRequestDate: equipment.demoRequestDate || undefined,
                demoApprovalDate: equipment.demoApprovalDate || undefined,
                demoStartDate: equipment.demoStartDate || undefined,
                demoEndDate: equipment.demoEndDate || undefined,
                demoRequestName: equipment.demoRequestName,
                demoRequestEmail: equipment.demoRequestEmail,
                systemDataJson: equipment.systemDataJson,
                location: equipment.location,
                building: equipment.building,
                region: equipment.region,
                country: equipment.country,
                city: equipment.city,
                departmentName: equipment.departmentName,
                outOfServiceDate: equipment.outOfServiceDate || undefined,
                repAssignDate: equipment.repAssignDate || undefined,
                salesRepId: equipment.salesRepId,
                score: equipment.score,
                shippingNotes: equipment.shippingNotes,
                stationNumber: equipment.stationNumber,
                address1: equipment.address1,
                address2: equipment.address2,
                state: equipment.state,
                postalCode: equipment.postalCode,
                contactName: equipment.contactName,
                contactTitle: equipment.contactTitle,
                contactEmail: equipment.contactEmail,
                qaCompleteDate: equipment.qaCompleteDate,
                receivedDate: equipment.receivedDate,
                shippedDate: equipment.shippedDate,
                demoDuration: equipment.demoDuration
            };

            await EquipmentService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.equipmentId === -1) {
                navigate(-1);
            }
        }
    };

    if (equipment) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <strong>{equipment.product?.name} ({equipment.product?.partNumber})</strong>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New Equipment</strong>
                                    }
                                </Card.Title>

                                <DPSEntitySelector<IProduct, IProductFilters>
                                    entityFilterSearchPropName={"names"}
                                    entityService={ProductService}
                                    entityTypeName="Product"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={product}
                                    generateEntityTitle={(entry) => entry.name}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Products/Detail/${entry.productId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, productId: newEntry?.productId || undefined });
                                        setProduct(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<IFireDepartment, IFireDepartmentFilters>
                                    entityFilterSearchPropName={"fireDeptNames"}
                                    entityService={FireDepartmentService}
                                    entityTypeName="Department"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={fireDepartment}
                                    generateEntityTitle={(entry) => `${entry.fireDeptName} ${entry.hqcity} ${entry.hqstate}, ${entry.hqzip}`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Departments/Detail/${entry.fireDepartmentId}`);
                                        }
                                    }}
                                    onClickAddNewHandler={() => {
                                        window.open(`/DPS/Departments/Detail/?defs={"fireDeptName":"${equipment.departmentName ?? ""}","hqstate":"","hqCountry":"${equipment.country ?? ""}"}`);
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, fireDepartmentId: newEntry?.fireDepartmentId || undefined });
                                        setFireDepartment(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<IFireStation, IFireStationFilters>
                                    entityFilterSearchPropName={"postalCodes"}
                                    entityService={FireStationService}
                                    entityTypeName="Station"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={fireStation}
                                    generateEntityTitle={(entry) => `${entry.name} (${entry.number})`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Stations/Detail/${entry.fireStationId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, stationId: newEntry?.fireStationId || undefined });
                                        setFireStation(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<IDealer, IDealerFilters>
                                    entityFilterSearchPropName={"dealerNames"}
                                    entityService={DealerService}
                                    entityTypeName="Dealer"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={dealer}
                                    generateEntityTitle={(entry) => entry.dealerName ?? ""}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Dealers/Detail/${entry.dealerId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, dealerId: newEntry?.dealerId || undefined });
                                        setDealer(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<ISalesRep, ISalesRepFilters>
                                    entityFilterSearchPropName={"names"}
                                    entityService={SalesRepService}
                                    entityTypeName="Rep"
                                    defaultEntityFilter={{ dealerIds: dealer ? [dealer.dealerId] : undefined }}
                                    selectedEntityRef={salesRep}
                                    generateEntityTitle={(entry) => entry?.fullName ?? ""}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Reps/Detail/${entry.salesRepId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setEquipment({ ...equipment, salesRepId: newEntry?.salesRepId || undefined });
                                        setSalesRep(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Serial Number</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, serialNumber: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.serialNumber}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Building</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, building: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.building}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Location</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, location: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.location}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Address 1</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, address1: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.address1}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Address 2</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, address2: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.address2}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>City</Form.Label>
                                            {
                                                equipment.fireDepartment &&
                                                <>
                                                    &nbsp;(Dept: {equipment.fireDepartment.hqcity})<br />
                                                </>
                                            }
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, city: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.city}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>State</Form.Label>
                                            {
                                                equipment.fireDepartment &&
                                                <>
                                                    &nbsp;(Dept: {equipment.fireDepartment.hqstate})<br />
                                                </>
                                            }
                                            {
                                                <StateChooser
                                                    onChange={newVal => {
                                                        setEquipment({ ...equipment, state: newVal });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    defaultValue={equipment.state}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Postal Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, postalCode: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.postalCode}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Country</Form.Label>
                                            {
                                                equipment.fireDepartment &&
                                                <>
                                                    &nbsp;(Dept: {equipment.fireDepartment.hqCountry})<br />
                                                </>
                                            }
                                            <CountryChooser
                                                onChange={newVal => {
                                                    setEquipment({ ...equipment, country: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                defaultValue={equipment.country}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Registration Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, registrationName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.registrationName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Registration Postal Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, registrationPostalCode: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.registrationPostalCode}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Registration Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, registrationEmail: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.registrationEmail}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Demo Request Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, demoRequestEmail: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.demoRequestEmail}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Temp Department Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, departmentName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.departmentName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setEquipment({ ...equipment, score: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={equipment.score || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="pb-3">
                                    <Col>
                                        <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                            <Accordion.Item
                                                eventKey="0"
                                                style={{
                                                    "backgroundColor": "#eee"
                                                }}>
                                                <Accordion.Header><strong>Dates / Timeline</strong></Accordion.Header>
                                                <Accordion.Body>
                                                    <Row>
                                                        <Col>
                                                            <Row className="align-items-end">

                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Initial (Created) Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, created: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.created ? moment(equipment.created).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>QA Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, qaCompleteDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.qaCompleteDate ? moment(equipment.qaCompleteDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Received Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, receivedDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.receivedDate ? moment(equipment.receivedDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Demo Request Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, demoRequestDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.demoRequestDate ? moment(equipment.demoRequestDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Demo Approval Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, demoApprovalDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.demoApprovalDate ? moment(equipment.demoApprovalDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Ship Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, shippedDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.shippedDate ? moment(equipment.shippedDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Demo Start Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, demoStartDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.demoStartDate ? moment(equipment.demoStartDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Demo End Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, demoEndDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.demoEndDate ? moment(equipment.demoEndDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Started Service</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, startedService: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.startedService ? moment(equipment.startedService).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Registration Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, registrationDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.registrationDate ? moment(equipment.registrationDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Out of Service Date</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="date"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setEquipment({ ...equipment, outOfServiceDate: e.target.value ? e.target.value : undefined });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={equipment.outOfServiceDate ? moment(equipment.outOfServiceDate).format('yyyy-MM-DD') : undefined}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                    </Row>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </Col>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                globalContextRef={globalContextRef.current}
                                                noun="Note"
                                                onChange={newVal => {
                                                    setEquipment({ ...equipment, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={equipment.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className="align-items-end">
                    <Col>
                        <Card className="mb-2">
                            <Card.Body>
                                <Card.Title>
                                    <strong>Attachments</strong><br />
                                </Card.Title>
                                {
                                    attachments && attachments.length > 0 ?
                                        attachments.map((attachment, index) => {
                                            return (
                                                <div key={index}>
                                                    <a href={`/api/Misc/downloadFile?fileName=${attachment.fileName}`} target="_blank">{attachment.title}</a>
                                                    <br />
                                                </div>
                                            );
                                        }) :
                                        <span>No attachments</span>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <Row className="align-items-end">
                        <Col>
                            <Card className="mb-2">
                                <Card.Body>
                                    <Card.Title>
                                        <strong>Log</strong><br />
                                    </Card.Title>
                                    {generateLog<IEquipment>(equipment)}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                }

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the Equipment, please try again.</h1>
            </>
        );
    }
}

export default EquipmentDetail;
